import React from 'react';
import PropTypes from 'prop-types';
import StructuredContent from '@nerdwallet/structured-content';
import {
  stringInputSpec,
  boolCheckboxSpec,
  stringSelectSpec,
  numberInputSpec,
} from '../../lib/editing-configs';

const generateInsuranceBanner = (
  vertical,
  title,
  description,
  buttonText,
  redirectToPartner,
  buttonAnnotation,
  pricingCode
) => {
  switch (vertical) {
    case 'life':
      return {
        type: 'LifeInsuranceBanner',
        version: '1.0.0',
        props: {
          title: title || 'Compare Life Insurance Rates',
          description:
            description ||
            'Get instant quotes from top Life Insurance companies and compare now.',
          buttonText,
          redirectToPartner,
          buttonAnnotation: buttonAnnotation || 'Compare with Policygenius',
        },
      };
    case 'home':
      return {
        type: 'HomeInsuranceBanner',
        version: '1.0.0',
        props: {
          title: title || 'Compare Home Insurance Rates',
          description:
            description ||
            'Get instant quotes from top home insurance companies and compare now.',
          buttonText,
          redirectToPartner,
          buttonAnnotation:
            buttonAnnotation || 'Matches Provided by Insure.com',
          pricingCode: pricingCode || '662940',
        },
      };
    default:
      return {
        type: 'AutoInsuranceBanner',
        version: '1.0.0',
        props: {
          title: title || 'Compare Car Insurance Rates',
          description:
            description ||
            'Get instant quotes from top auto insurance companies and compare now.',
          buttonText,
          redirectToPartner,
          buttonAnnotation:
            buttonAnnotation || 'Matches Provided by Insure.com',
          pricingCode: pricingCode || '664412',
        },
      };
  }
};

const InsuranceStickyWidget = ({
  vertical,
  triggeringScrollDepth,
  stickTo,
  spacing,
  title,
  description,
  buttonText,
  redirectToPartner,
  buttonAnnotation,
  pricingCode,
}) => {
  return (
    <StructuredContent noClearFix>
      {{
        type: 'StickyContainer',
        version: '1.0.0',
        props: {
          stickTo,
          spacing,
          children: [
            {
              type: 'ScrollDepthConditional',
              version: '1.0.0',
              props: {
                triggeringScrollDepth,
                children: [
                  generateInsuranceBanner(
                    vertical,
                    title,
                    description,
                    buttonText,
                    redirectToPartner,
                    buttonAnnotation,
                    pricingCode
                  ),
                ],
              },
            },
          ],
        },
      }}
    </StructuredContent>
  );
};

InsuranceStickyWidget.propTypes = {
  vertical: PropTypes.oneOf(['auto', 'home', 'life']),
  triggeringScrollDepth: PropTypes.number,
  stickTo: PropTypes.oneOf(['top', 'bottom']),
  spacing: PropTypes.oneOf(['0', '1', '2', '3', '3-half', '4', '5']),
  title: PropTypes.string,
  description: PropTypes.string,
  buttonText: PropTypes.string,
  redirectToPartner: PropTypes.bool,
  buttonAnnotation: PropTypes.string,
  pricingCode: PropTypes.string,
};

InsuranceStickyWidget.defaultProps = {
  vertical: 'AUTO',
  stickTo: 'top',
  spacing: '0',
  buttonText: 'Get Started',
  redirectToPartner: false,
};

InsuranceStickyWidget.getEditingConfig = () => {
  return [
    stringSelectSpec('vertical', 'Type of Insurance', [
      { label: 'Auto', value: 'auto' },
      { label: 'Home', value: 'home' },
      { label: 'Life', value: 'life' },
    ]),
    stringInputSpec('title', 'Title'),
    stringInputSpec('description', 'Description'),
    stringInputSpec('buttonText', 'Button Text'),
    boolCheckboxSpec('redirectToPartner', 'Redirect To Partner'),
    stringInputSpec('buttonAnnotation', 'Button Annotation'),
    stringInputSpec('pricingCode', 'Pricing Code'),
    stringSelectSpec(
      'stickTo',
      'Stick To Alignment',
      [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
      ],
      {
        help: 'The edge the banner will stick to until it is scrolled past in the document',
      }
    ),
    stringSelectSpec(
      'spacing',
      'Spacing',
      [
        { label: '0', value: '0' },
        { label: '1', value: '1' },
        { label: '2', value: '2' },
        { label: '3', value: '3' },
        { label: '3-half', value: '3-half' },
        { label: '4', value: '4' },
        { label: '5', value: '5' },
      ],
      {
        help: 'The amount of space between the edge and the banner',
      }
    ),
    numberInputSpec(
      'triggeringScrollDepth',
      'Triggering Scroll Depth Percentage',
      {
        help: 'Defaults to 0',
      }
    ),
  ];
};

InsuranceStickyWidget.displayName = 'InsuranceStickyWidget';

export default InsuranceStickyWidget;
